import { motion } from 'framer-motion';
import { useInView } from '@/utils/animations';
import { Button } from '@/components/ui/button';

const About = () => {
  const { ref, isInView } = useInView(0.2);

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="about" className="py-24" ref={ref as React.RefObject<HTMLDivElement>}>
      <div className="container">
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          <motion.div
            className="lg:w-1/2"
            variants={fadeInUpVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              About <span className="text-primary">me</span>
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                I am a software engineer with roots in embedded systems and a growing range across the
                modern stack.
              </p>
              <p>
                My first three years were spent in production firmware at Pfeiffer Vacuum, writing C/C++
                for industrial motion controllers, debugging with oscilloscopes, tuning PID algorithms,
                and owning customer escalations for field issues. I learned that good engineering often means
                making hard problems disappear quietly.
              </p>
              <p>
                Since then I have built on that foundation, shipping web and mobile apps with React,
                TypeScript, FastAPI, and Supabase, and integrating AI where it makes the product more useful.
                I work end-to-end: database schema, backend logic, and a polished UI.
              </p>
              <p>
                I am a first-generation college graduate, bilingual in English and Spanish, and I build with
                the same care whether the audience is a team of one or thousands of users.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button variant="outline" asChild>
                <a href="/files/JenaroVegaBecerrilResumePublic2026.pdf" download="JenaroVegaBecerrilResumePublic2026.pdf">
                  Download resume
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="#contact">Get in touch</a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="lg:w-1/2 glass-card p-8 space-y-6"
            variants={fadeInUpVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            transition={{ delay: 0.2 }}
          >
            <div>
              <h3 className="text-xl font-semibold mb-4">At a glance</h3>
              <div className="columns-1 sm:columns-2 gap-x-8 gap-y-6 [column-fill:_balance]">
                <div className="break-inside-avoid mb-6">
                  <h4 className="font-medium mb-2 text-foreground">Embedded & hardware</h4>
                  <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-4 space-y-1.5">
                    <li>C/C++, Python (test automation, internal tools, hardware workflows)</li>
                    <li>Assembly (basic)</li>
                    <li>EtherCAT, BLE, UART, I2C, SPI</li>
                    <li>TMS320F28379D, PID, oscilloscope, DAQ, soldering</li>
                  </ul>
                </div>
                <div className="break-inside-avoid mb-6">
                  <h4 className="font-medium mb-2 text-foreground">Web & mobile</h4>
                  <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-4 space-y-1.5">
                    <li>React, React Native / Expo, TypeScript, JavaScript</li>
                    <li>FastAPI, WebSockets, REST APIs</li>
                  </ul>
                </div>
                <div className="break-inside-avoid mb-6">
                  <h4 className="font-medium mb-2 text-foreground">Data & cloud</h4>
                  <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-4 space-y-1.5">
                    <li>PostgreSQL, Supabase, MongoDB</li>
                    <li>Serverless functions, RLS, authentication</li>
                  </ul>
                </div>
                <div className="break-inside-avoid mb-6">
                  <h4 className="font-medium mb-2 text-foreground">AI & tools</h4>
                  <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-4 space-y-1.5">
                    <li>OpenAI APIs</li>
                    <li>Claude Code, Cursor, Codex</li>
                    <li>Git, Jira, Pytest, Figma</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4">Education</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">B.S. Computer Science, embedded systems focus</h4>
                  <p className="text-muted-foreground">University of California, Riverside · March 2021</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
